import { useEffect, useMemo, useRef, useState } from "react"
import {
  PaymentMethodTypeEnum,
  type PaymentMethodType,
} from "@/plugins/stub/billing-data/utils"

export type CollectJsTokenizationConfig = {
  tokenSrc: string | null
  scriptProperties: Record<string, string | null> | null
}

export type CollectJsTokenizedPayment = {
  token: string
  paymentMethodType: PaymentMethodType
  cardBrand: string | null
  cardCategory: string | null
  cardLast4: string | null
  cardExpMonth: string | null
  cardExpYear: string | null
  achAccountLast4: string | null
  achRoutingLast4: string | null
}

type CollectJsPaymentFormProps = {
  paymentMethodType: PaymentMethodType
  config: CollectJsTokenizationConfig | null
  tokenRequestNonce: number
  showSaveAndSetDefaultOption?: boolean
  saveAndSetDefault?: boolean
  onSaveAndSetDefaultChange?: (enabled: boolean) => void
  onReadyChange: (ready: boolean) => void
  onCardCategoryChange?: (category: string | null) => void
  onTokenized: (result: CollectJsTokenizedPayment) => void
  onError: (message: string) => void
}

type CollectJsApi = {
  configure: (options: Record<string, unknown>) => void
  startPaymentRequest: (event?: Event) => void
  clearInputs?: () => void
}

type CollectJsCardResponse = {
  number?: string | null
  bin?: string | null
  exp?: string | null
  type?: string | null
  category?: string | null
  hash?: string | null
}

declare global {
  interface Window {
    CollectJS?: CollectJsApi
  }
}

function normalizeDigits(value: string | null | undefined) {
  return (value ?? "").replace(/\D/g, "")
}

function parseCardExpiration(rawExp: string | null | undefined) {
  const digits = normalizeDigits(rawExp)

  if (digits.length < 4) {
    return { month: null, year: null }
  }

  const month = digits.slice(0, 2)
  const year = digits.length === 4 ? `20${digits.slice(2, 4)}` : digits.slice(2, 6)
  return { month, year }
}

function getDataAttribute(config: CollectJsTokenizationConfig | null, key: string) {
  return config?.scriptProperties?.[key] ?? null
}

export function CollectJsPaymentForm({
  paymentMethodType,
  config,
  tokenRequestNonce,
  showSaveAndSetDefaultOption = false,
  saveAndSetDefault = false,
  onSaveAndSetDefaultChange,
  onReadyChange,
  onCardCategoryChange,
  onTokenized,
  onError,
}: CollectJsPaymentFormProps) {
  const [scriptLoaded, setScriptLoaded] = useState(false)
  const [, setIsConfigured] = useState(false)
  const tokenSrc = config?.tokenSrc ?? null
  const scriptProperties = config?.scriptProperties ?? null
  const tokenizationKey = getDataAttribute(config, "data-tokenization-key")
  const variant = getDataAttribute(config, "data-variant") ?? "inline"
  const currency = getDataAttribute(config, "data-currency") ?? "USD"
  const country = getDataAttribute(config, "data-country") ?? "US"
  const price = getDataAttribute(config, "data-price") ?? "1.00"
  const lastTokenRequestNonce = useRef(0)
  const activeTokenRequestNonce = useRef<number | null>(null)
  const tokenRequestRetryCount = useRef<Record<number, number>>({})
  const configureRunId = useRef(0)
  const onReadyChangeRef = useRef(onReadyChange)
  const onCardCategoryChangeRef = useRef(onCardCategoryChange)
  const onTokenizedRef = useRef(onTokenized)
  const onErrorRef = useRef(onError)

  useEffect(() => {
    onReadyChangeRef.current = onReadyChange
    onCardCategoryChangeRef.current = onCardCategoryChange
    onTokenizedRef.current = onTokenized
    onErrorRef.current = onError
  }, [onCardCategoryChange, onError, onReadyChange, onTokenized])

  const idPrefix = useMemo(() => `collectjs-${Math.random().toString(36).slice(2, 10)}`, [])
  const cardNumberId = `${idPrefix}-ccnumber`
  const cardExpId = `${idPrefix}-ccexp`
  const cardCvvId = `${idPrefix}-cvv`
  const achNameId = `${idPrefix}-checkname`
  const achAccountId = `${idPrefix}-checkaccount`
  const achRoutingId = `${idPrefix}-checkaba`

  function clearCollectInputsSafely() {
    try {
      window.CollectJS?.clearInputs?.()
    } catch {
      // Collect.js may throw during rapid reconfigure/unmount; continue with host cleanup fallback.
    }
  }

  function clearFieldHosts() {
    ;[cardNumberId, cardExpId, cardCvvId, achNameId, achAccountId, achRoutingId].forEach((id) => {
      try {
        document.getElementById(id)?.replaceChildren()
      } catch {
        // Ignore missing/stale host nodes during mode switches.
      }
    })
  }

  useEffect(() => {
    onReadyChangeRef.current(false)
    onCardCategoryChangeRef.current?.(null)
    setScriptLoaded(false)
    setIsConfigured(false)

    if (!tokenSrc || !tokenizationKey) {
      onErrorRef.current("Missing payment tokenization settings.")
      return
    }

    const scriptId = "cloudfit-collectjs-script"
    const existingScript = document.getElementById(scriptId) as HTMLScriptElement | null

    if (existingScript && window.CollectJS) {
      setScriptLoaded(true)
      return
    }

    if (existingScript) {
      existingScript.addEventListener("load", () => setScriptLoaded(true), { once: true })
      existingScript.addEventListener("error", () => onErrorRef.current("Failed to load secure payment fields."), { once: true })
      return
    }

    const script = document.createElement("script")
    script.id = scriptId
    script.src = tokenSrc
    script.async = true

    Object.entries(scriptProperties ?? {}).forEach(([key, value]) => {
      if (value) {
        script.setAttribute(key, value)
      }
    })

    script.addEventListener("load", () => setScriptLoaded(true), { once: true })
    script.addEventListener("error", () => onErrorRef.current("Failed to load secure payment fields."), { once: true })
    document.head.appendChild(script)
  }, [tokenSrc, tokenizationKey])

  useEffect(() => {
    if (!scriptLoaded || !window.CollectJS) {
      return
    }

    const runId = configureRunId.current + 1
    configureRunId.current = runId

    onReadyChangeRef.current(false)
    setIsConfigured(false)
    clearCollectInputsSafely()
    clearFieldHosts()

    const fields =
      paymentMethodType === PaymentMethodTypeEnum.Card
        ? {
            ccnumber: {
              selector: `#${cardNumberId}`,
              title: "Card Number",
              placeholder: "0000 0000 0000 0000",
            },
            ccexp: {
              selector: `#${cardExpId}`,
              title: "Expiration Date",
              placeholder: "MM / YY",
            },
            cvv: {
              selector: `#${cardCvvId}`,
              title: "Security Code",
              placeholder: "CVV",
            },
          }
        : {
            checkname: { selector: `#${achNameId}`, title: "Account Holder Name" },
            checkaccount: { selector: `#${achAccountId}`, title: "Account Number" },
            checkaba: { selector: `#${achRoutingId}`, title: "Routing Number" },
          }

    window.CollectJS.configure({
      variant,
      price,
      currency,
      country,
      placeholderCss: {
        "font-size": "16px",
        "line-height": "22px",
      },
      validCss: {
        "font-size": "16px",
        "line-height": "22px",
        "white-space": "nowrap",
      },
      invalidCss: {
        "font-size": "16px",
        "line-height": "22px",
        "white-space": "nowrap",
      },
      focusCss: {
        "font-size": "16px",
        "line-height": "22px",
        "white-space": "nowrap",
      },
      fields,
      fieldsAvailableCallback: () => {
        if (configureRunId.current !== runId) {
          return
        }

        setIsConfigured(true)
        onReadyChangeRef.current(true)
      },
      timeoutDuration: 20000,
      timeoutCallback: () => {
        if (configureRunId.current !== runId) {
          return
        }

        const requestNonce = activeTokenRequestNonce.current

        if (requestNonce !== null) {
          const retryCount = tokenRequestRetryCount.current[requestNonce] ?? 0

          if (retryCount < 1) {
            tokenRequestRetryCount.current[requestNonce] = retryCount + 1
            window.CollectJS?.startPaymentRequest()
            return
          }
        }

        onReadyChangeRef.current(false)
        onCardCategoryChangeRef.current?.(null)
        setIsConfigured(false)
        onErrorRef.current("Payment tokenization timed out. Please check the fields and try again.")
      },
      lookUpCallback: (response: CollectJsCardResponse) => {
        if (configureRunId.current !== runId) {
          return
        }

        onCardCategoryChangeRef.current?.(response.category ?? null)
      },
      callback: (response: Record<string, unknown>) => {
        if (configureRunId.current !== runId) {
          return
        }

        if (activeTokenRequestNonce.current !== null) {
          delete tokenRequestRetryCount.current[activeTokenRequestNonce.current]
        }
        activeTokenRequestNonce.current = null

        const token = (response.token as string | undefined) ?? ""

        if (!token) {
          onErrorRef.current("Tokenization did not return a payment token.")
          return
        }

        const card = (response.card as Record<string, unknown> | undefined) ?? {}
        const check = (response.check as Record<string, unknown> | undefined) ?? {}
        const cardExp = parseCardExpiration(card.exp as string | null | undefined)
        const cardCategory = (card.category as string | undefined) ?? null

        onCardCategoryChangeRef.current?.(cardCategory)

        onTokenizedRef.current({
          token,
          paymentMethodType,
          cardBrand: (card.type as string | undefined) ?? null,
          cardCategory,
          cardLast4: normalizeDigits(card.number as string | null | undefined).slice(-4) || null,
          cardExpMonth: cardExp.month,
          cardExpYear: cardExp.year,
          achAccountLast4: normalizeDigits(check.account as string | null | undefined).slice(-4) || null,
          achRoutingLast4: normalizeDigits((check.aba as string | null | undefined) ?? (check.transit as string | null | undefined)).slice(-4) || null,
        })
      },
    })

    return () => {
      if (configureRunId.current === runId) {
        configureRunId.current += 1
      }

      onReadyChangeRef.current(false)
      onCardCategoryChangeRef.current?.(null)
      clearCollectInputsSafely()
      clearFieldHosts()
    }
  }, [
    achAccountId,
    achNameId,
    achRoutingId,
    cardCvvId,
    cardExpId,
    cardNumberId,
    paymentMethodType,
    scriptLoaded,
    country,
    currency,
    price,
    variant,
    tokenizationKey,
  ])

  useEffect(() => {
    if (!window.CollectJS || tokenRequestNonce <= 0 || tokenRequestNonce === lastTokenRequestNonce.current) {
      return
    }

    lastTokenRequestNonce.current = tokenRequestNonce
    activeTokenRequestNonce.current = tokenRequestNonce
    tokenRequestRetryCount.current[tokenRequestNonce] = 0
    window.CollectJS?.startPaymentRequest()
  }, [tokenRequestNonce])

  return (
    <div className="space-y-3 rounded-md border bg-muted/20 p-3">
      <p className="text-xs text-muted-foreground">
        Sensitive payment details are collected securely by NMI and never touch this application directly.
      </p>

      {paymentMethodType === "card" ? (
        <>
          <label className="flex flex-col gap-1 text-sm">
            <span className="font-medium">Card number</span>
            <div id={cardNumberId} className="h-10 overflow-hidden whitespace-nowrap rounded-md border bg-background px-2 py-1" />
          </label>
          <div className="grid gap-3 md:grid-cols-2">
            <label className="flex flex-col gap-1 text-sm">
              <span className="font-medium">Expiration date</span>
              <div id={cardExpId} className="min-h-9 rounded-md border bg-background px-2 py-1" />
            </label>
            <label className="flex flex-col gap-1 text-sm">
              <span className="font-medium">Security code</span>
              <div id={cardCvvId} className="min-h-9 rounded-md border bg-background px-2 py-1" />
            </label>
          </div>
        </>
      ) : (
        <>
          <label className="flex flex-col gap-1 text-sm">
            <span className="font-medium">Account holder name</span>
            <div id={achNameId} className="min-h-9 rounded-md border bg-background px-2 py-1" />
          </label>
          <label className="flex flex-col gap-1 text-sm">
            <span className="font-medium">Bank account number</span>
            <div id={achAccountId} className="min-h-9 rounded-md border bg-background px-2 py-1" />
          </label>
          <label className="flex flex-col gap-1 text-sm">
            <span className="font-medium">Routing number</span>
            <div id={achRoutingId} className="min-h-9 rounded-md border bg-background px-2 py-1" />
          </label>
        </>
      )}

      {showSaveAndSetDefaultOption ? (
        <label className="flex items-center gap-2 text-sm">
          <input
            type="checkbox"
            checked={saveAndSetDefault}
            onChange={(event) => onSaveAndSetDefaultChange?.(event.target.checked)}
          />
          Save this payment method for future payments and set it as the default.
        </label>
      ) : null}
    </div>
  )
}
